import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deferredBookingInquiryService } from '../services/deferredBookingInquiryService';
import type { DeferredBookingDecisionRequest } from '../types/deferredBookingInquiry';
import type { DeferredInquiryDecisionStatus } from '../types/supportCase';

export const DEFERRED_BOOKING_INQUIRY_KEYS = {
  all: ['deferred-booking-inquiries'] as const,
  pending: () => [...DEFERRED_BOOKING_INQUIRY_KEYS.all, 'pending'] as const,
  list: (params?: {
    decisionStatus?: ReadonlyArray<DeferredInquiryDecisionStatus>;
    page?: number;
    size?: number;
    sort?: string;
  }) => [...DEFERRED_BOOKING_INQUIRY_KEYS.all, 'list', params] as const,
};

export const usePendingDeferredBookingInquiries = () =>
  useQuery({
    queryKey: DEFERRED_BOOKING_INQUIRY_KEYS.pending(),
    queryFn: () => deferredBookingInquiryService.listPendingInquiries(),
  });

/** Fetches all inquiries (pending + decided) via the B2-published listing endpoint. */
export const useDeferredBookingInquiryList = (params?: {
  decisionStatus?: ReadonlyArray<DeferredInquiryDecisionStatus>;
  page?: number;
  size?: number;
  sort?: string;
}) =>
  useQuery({
    queryKey: DEFERRED_BOOKING_INQUIRY_KEYS.list(params),
    queryFn: () => deferredBookingInquiryService.listInquiries(params),
  });

export const useDecideDeferredBookingInquiry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      bookingId,
      request,
    }: {
      bookingId: string;
      request: DeferredBookingDecisionRequest;
    }) => deferredBookingInquiryService.decideInquiry(bookingId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEFERRED_BOOKING_INQUIRY_KEYS.all });
    },
  });
};
